import { apiClient } from '@/shared/services/api';
import { retryWithBackoff } from '@/shared/utils/retryWithBackoff';

/**
 * Récupère les notifications de l'utilisateur
 * @param {number} skip - Nombre d'éléments à ignorer (pagination)
 * @param {number} take - Nombre d'éléments à récupérer
 * @param {boolean} includeRead - Inclure les notifications lues
 * @returns {Promise<{success: boolean, message: string, data: Array}>}
 */
export const getNotifications = async (skip = 0, take = 10, includeRead = true) => {
  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(async () => {
    return await apiClient.get('/notifications', {
      params: {
        skip,
        take,
        includeRead
      }
    });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data || [];
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching notifications';
  });

  return returnObject;
};

/**
 * Notifications v2 — grouped/collapsed list with optional category filter.
 * @param {number} skip
 * @param {number} take
 * @param {boolean} includeRead
 * @param {string|null|undefined} category - messages|social|stories|sports|groups|pages|live|system
 */
export const getNotificationsV2 = async (skip = 0, take = 10, includeRead = true, category = null) => {
  const returnObject = { success: false, message: '', data: null, meta: null };

  const params = { skip, take, includeRead };
  if (category) params.category = category;

  await retryWithBackoff(async () => {
    return await apiClient.get('/notifications/v2', { params });
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data || [];
    returnObject.meta = response.data.meta || null;
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed fetching notifications';
  });

  return returnObject;
};

/**
 * Marque une notification comme lue
 * @param {string} id - ID de la notification
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const markNotificationAsRead = async (id) => {
  const returnObject = { success: false, message: '' };

  const candidateRequests = [
    { method: 'patch', url: `/notifications/${id}/read` },
    { method: 'post', url: `/notifications/${id}/read` },
    { method: 'put', url: `/notifications/${id}/read` },
    { method: 'patch', url: `/notifications/read/${id}` },
    { method: 'post', url: `/notifications/read/${id}` },
    { method: 'put', url: `/notifications/read/${id}` }
  ];

  let lastError = null;

  for (const requestConfig of candidateRequests) {
    try {
      const response = await retryWithBackoff(async () => {
        return await apiClient.request(requestConfig);
      });

      returnObject.success = true;
      returnObject.message = response?.data?.message || 'Notification marked as read';
      return returnObject;
    } catch (error) {
      lastError = error;
      const status = Number(error?.response?.status || 0);
      const isRouteOrMethodMismatch = status === 404 || status === 405;

      // Try next candidate only for route/method mismatches.
      if (isRouteOrMethodMismatch) {
        continue;
      }
      break;
    }
  }

  returnObject.success = false;
  returnObject.message =
    lastError?.response?.data?.message ||
    lastError?.message ||
    'Failed to mark notification as read';

  return returnObject;
};

/**
 * Marque toutes les notifications comme lues
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const markAllNotificationsAsRead = async () => {
  const returnObject = { success: false, message: '' };

  await retryWithBackoff(async () => {
    return await apiClient.post('/notifications/mark-all-read');
  }).then((response) => {
    returnObject.success = true;
    returnObject.message = response.data.message || 'All notifications marked as read';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed to mark all notifications as read';
  });

  return returnObject;
};

/**
 * Supprime une notification
 * @param {string} id - ID de la notification
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteNotification = async (id) => {
  const returnObject = { success: false, message: '' };

  await retryWithBackoff(async () => {
    return await apiClient.delete(`/notifications/${id}`);
  }).then((response) => {
    returnObject.success = true;
    returnObject.message = response.data.message || 'Notification deleted';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed to delete notification';
  });

  return returnObject;
};

/**
 * Supprime toutes les notifications
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteAllNotifications = async () => {
  const returnObject = { success: false, message: '' };

  await retryWithBackoff(async () => {
    return await apiClient.delete('/notifications/delete/all');
  }).then((response) => {
    returnObject.success = true;
    returnObject.message = response.data.message || 'All notifications deleted';
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed to delete all notifications';
  });

  return returnObject;
};

/**
 * Récupère le nombre de notifications non lues
 * @returns {Promise<{success: boolean, message: string, data: {count: number}}>}
 */
export const getUnreadNotificationCount = async () => {
  const returnObject = { success: false, message: '', data: { count: 0 } };

  await retryWithBackoff(async () => {
    return await apiClient.get('/notifications/unread-count');
  }).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data.data || { count: 0 };
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message = error?.response?.data?.message || 'Failed to get unread count';
  });

  return returnObject;
};
